import React, { useEffect, useMemo, useRef, useState } from "react";
import { Database, Pause, Play, Clock } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { useLive } from "@/store/LiveContext";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading } from "@/components/common";
import { num } from "@/lib/format";

const TIME_COLS = new Set(["ts", "created", "timestamp", "issued_at"]);

function rowKey(r) {
  return r.ts || r.created || r.timestamp || r._id || JSON.stringify(r);
}

function fmtCell(col, v) {
  if (v == null || v === "") return <span className="text-slate-300">—</span>;
  if (TIME_COLS.has(col)) {
    const d = new Date(v);
    if (!isNaN(d)) return d.toLocaleTimeString("en-IN", { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
  }
  if (typeof v === "boolean") return v ? <span className="text-emerald-600">✓</span> : <span className="text-red-500">✗</span>;
  if (typeof v === "number") return num(v, Number.isInteger(v) ? 0 : 3);
  return String(v);
}

export default function DBInspector() {
  const { data: meta } = usePoll(endpoints.dbCollections, 8000);
  const { data: live } = useLive();
  const [selected, setSelected] = useState("uodl_history");
  const [limit, setLimit] = useState(60);
  const [paused, setPaused] = useState(false);

  const { data } = usePoll(
    () => endpoints.dbCollection(selected, limit),
    paused ? 0 : 2000,
    [selected, limit, paused]
  );

  const prevKeys = useRef(new Set());
  const [flash, setFlash] = useState(new Set());

  useEffect(() => { prevKeys.current = new Set(); setFlash(new Set()); }, [selected]);

  useEffect(() => {
    if (!data?.rows) return;
    const cur = data.rows.map(rowKey);
    const isNew = cur.filter((k) => !prevKeys.current.has(k));
    if (prevKeys.current.size && isNew.length) {
      setFlash(new Set(isNew));
      const t = setTimeout(() => setFlash(new Set()), 1800);
      prevKeys.current = new Set(cur);
      return () => clearTimeout(t);
    }
    prevKeys.current = new Set(cur);
  }, [data]);

  const columns = data?.columns || [];

  // Build the LIVE (in-flight, not yet persisted) row for uodl_history from the WS tick
  const liveRow = useMemo(() => {
    if (selected !== "uodl_history" || !live?.ribbons) return null;
    const e = live.ribbons.economics, p = live.ribbons.production, s = live.ribbons.sustainability, a = live.ribbons.alerts;
    return {
      ts: live.timestamp, mode: live.mode,
      "m.utility_per_l": e.utility_per_l, "m.margin_l": e.margin_per_l,
      "m.refrigeration_per_l": e.refrigeration_per_l, "m.steam_per_l": e.steam_per_l,
      "m.carbon_l": s.carbon_intensity, "m.energy_l": s.energy_intensity,
      "m.production_klpd": p.milk_klpd, "m.opportunity_yr": e.opportunity_yr,
      "alerts.critical": a.critical, "alerts.high": a.high, "alerts.medium": a.medium,
    };
  }, [live, selected]);

  const collections = meta?.collections || [];
  const current = collections.find((c) => c.name === selected);

  return (
    <div data-testid="db-inspector-page">
      <PageHeader icon={Database} title="DB Inspector"
        subtitle={`MongoDB · ${meta?.database || "ergoniq_oim"} — live append-only log, auto-refreshing every 2s.`}
        right={
          <div className="flex items-center gap-2">
            <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value))} data-testid="db-limit-select"
              className="text-xs font-semibold border border-slate-200 rounded px-2 py-1.5 focus:outline-none">
              {[30, 60, 120, 250].map((n) => <option key={n} value={n}>{n} rows</option>)}
            </select>
            <button onClick={() => setPaused((p) => !p)} data-testid="db-pause-btn"
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded transition-colors ${paused ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-700"}`}>
              {paused ? <><Play size={13} /> Paused</> : <><span className="w-2 h-2 rounded-full bg-emerald-500 live-dot" /> Streaming</>}
            </button>
          </div>
        } />

      {/* Collection selector */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="db-collections">
        {collections.map((c) => (
          <button key={c.name} onClick={() => setSelected(c.name)} data-testid={`db-coll-${c.name}`}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
              selected === c.name ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}>
            <Database size={14} className={selected === c.name ? "text-white" : "text-slate-400"} />
            <span className="font-medium">{c.name}</span>
            <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${selected === c.name ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{c.count.toLocaleString("en-IN")}</span>
            {c.ttl_seconds && (
              <span className="flex items-center gap-0.5 text-[10px] text-amber-500" title={`TTL ${c.ttl_seconds / 86400} days`}>
                <Clock size={10} /> {c.ttl_seconds / 86400}d
              </span>
            )}
          </button>
        ))}
      </div>

      <Panel testid="db-table-panel"
        title={selected}
        subtitle={current ? `${data?.rows?.length || 0} shown of ${current.count.toLocaleString("en-IN")} documents${current.ttl_seconds ? ` · ${current.ttl_seconds / 86400}-day TTL` : ""}` : ""}
        right={!paused && <span className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600"><span className="w-2 h-2 rounded-full bg-emerald-500 live-dot" /> LIVE</span>}>
        {!data ? <Loading label="Loading collection…" /> : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[12px] border-collapse" data-testid="db-table">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left font-semibold text-slate-400 px-2 py-2 sticky left-0 bg-slate-50 border-b border-slate-200">#</th>
                  {columns.map((c) => (
                    <th key={c} className="text-left font-semibold text-slate-500 px-2.5 py-2 whitespace-nowrap border-b border-slate-200 font-mono">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="font-mono">
                {liveRow && (
                  <tr className="live-row border-b border-blue-100" data-testid="db-live-row">
                    <td className="px-2 py-1.5 text-blue-600 font-bold sticky left-0 bg-blue-50/40">●</td>
                    {columns.map((c) => (
                      <td key={c} className="px-2.5 py-1.5 whitespace-nowrap text-blue-900">
                        {c === "ts" ? <span className="font-semibold">LIVE · {fmtCell("ts", liveRow.ts)}</span> : fmtCell(c, liveRow[c])}
                      </td>
                    ))}
                  </tr>
                )}
                {data.rows.map((r, i) => {
                  const k = rowKey(r);
                  return (
                    <tr key={k} className={`border-b border-slate-50 hover:bg-slate-50 ${flash.has(k) ? "flash-row" : ""}`}>
                      <td className="px-2 py-1.5 text-slate-300 sticky left-0 bg-white">{i + 1}</td>
                      {columns.map((c) => (
                        <td key={c} className="px-2.5 py-1.5 whitespace-nowrap text-slate-700">{fmtCell(c, r[c])}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <p className="mt-3 text-[11px] text-slate-400">
        The top blue row is the in-flight runtime tick (pushed over WebSocket, ~every 5s) before it is persisted.
        New persisted rows append at the top and flash green. <span className="font-mono">uodl_history</span> auto-expires after 7 days (TTL index).
      </p>
    </div>
  );
}
